import { find } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { groupsApi, tradersApi, usersApi } from 'api';
import {
  DataGridColumnDefinition,
  MailLink,
  StylizedNumber,
  UserInfo,
  dataGridColumns,
  CrudPage,
  CopyTextId,
  InfoTooltip,
  CommissionInfo,
  IntervalCommissionsInfoDialog,
} from 'components';
import { ROUTE_PATH } from 'constants/routes';
import { FilterDefinitionType, QueryKey, TraderWorkStatus } from 'enums';
import { useMeta, usePartialQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  AgentInfo,
  FilterDefinition,
  Group,
  IntervalCommission,
  Trader,
  TraderFilters,
} from 'types';
import { filtersUtils, formatUtils, formUtils, traderUtils } from 'utils';

import { TraderStatus } from './TraderStatus';

export const TradersPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.traders',
  });
  const { t: tCommon } = useTranslation();

  const { payoutMaxActiveOrders } = useMeta();

  const { role, isAdmin, isTechOperator } = useUser();
  const canManage = useMemo(
    () => isAdmin || isTechOperator,
    [isAdmin, isTechOperator],
  );

  const traderDetailsRoutePath = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.TRADER_DETAILS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.TRADER_DETAILS;
    }
  }, [isAdmin, isTechOperator]);

  const navigate = useNavigate();
  const queryResult = usePartialQuery(
    QueryKey.Traders,
    tradersApi.getAllPaginatedAsRole(role),
  );

  const queryResultAgents = useQuery(QueryKey.Agents, usersApi.getAllAgents);

  const queryResultGroups = useQuery(QueryKey.Groups, groupsApi.getAll, {
    enabled: canManage,
  });

  const queryResultTraders = useQuery([QueryKey.Traders, 'all'], () =>
    tradersApi.getAllAsRole(role)(),
  );

  const [
    intervalCommissionsInfoDialogProps,
    setIntervalCommissionsInfoDialogOpen,
  ] = useState<{
    open: boolean;
    intervalCommissions?: IntervalCommission[];
  }>({ open: false });

  const handleEditClick = useCallback(
    (traderId: string) => {
      const url = generatePath(traderDetailsRoutePath!, {
        id: traderId,
      });
      navigate(url);
    },
    [traderDetailsRoutePath, navigate],
  );

  const getAgentName = useCallback(
    (agentId: string) => {
      const agentUser = find(
        queryResultAgents.data,
        (user) => user.id === agentId,
      );
      return agentUser?.name || agentId;
    },
    [queryResultAgents],
  );

  const renderLimitsRow = useCallback(
    (label: string, value?: number, tooltip?: string) => {
      if (value) {
        return (
          <div className="tw-flex tw-align-middle">
            {`${label}: ${formatUtils.formatNumber(value)}`}
            {tooltip && <InfoTooltip tooltip={tooltip} />}
          </div>
        );
      }
    },
    [],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<Trader>[] => [
      {
        header: t('fields.id'),
        valueGetter: (item) => <CopyTextId id={item.id} />,
      },
      {
        header: t('fields.name'),
        valueGetter: (item) => (
          <Fragment>
            <UserInfo name={item.user?.name} id={item.user?.id} />
            {item.user?.email && <MailLink email={item.user?.email} />}
          </Fragment>
        ),
      },
      {
        header: t('fields.work_status'),
        valueGetter: (item) => <TraderStatus trader={item} />,
      },
      {
        header: (
          <Fragment>
            <div>{t('fields.compensation')}</div>
            <div>{`(${tCommon('common.payin')} / ${tCommon(
              'common.payout',
            )})`}</div>
          </Fragment>
        ),
        valueGetter: (item) => (
          <CommissionInfo
            commission={item.compensation}
            payoutCommission={item.payoutCompensation}
            intervalCommissions={item.intervalCommissions}
            onIntervalCommissionsButtonClick={(e: React.SyntheticEvent) => {
              e.stopPropagation();
              setIntervalCommissionsInfoDialogOpen({
                open: true,
                intervalCommissions: item.intervalCommissions,
              });
            }}
          />
        ),
      },
      {
        header: (
          <Fragment>
            <div>{t('fields.agents')}</div>
            <div>{`(${tCommon('common.payin')} / ${tCommon(
              'common.payout',
            )})`}</div>
          </Fragment>
        ),
        valueClassName: 'tw-min-w-[140px]',
        multiValueRenderer: {
          itemsGetter: (item) => item.user?.agents,
          valueGetter: (agent: AgentInfo) => (
            <div key={agent.userId} className="tw-flex tw-flex-wrap">
              <span className="tw-pr-2">{getAgentName(agent.userId)}</span>
              <StylizedNumber
                value={agent.payinCompensationPercentage || 0}
                unit="%"
              />
              <span className="tw-px-1">{'/'}</span>
              <StylizedNumber
                value={agent.payoutCompensationPercentage || 0}
                unit="%"
              />
            </div>
          ),
        },
      },
      {
        header: tCommon('common.payin'),
        valueGetter: (item) => (
          <Fragment>
            {renderLimitsRow(
              t('fields.min_order_amount_short'),
              item.payinMinOrderAmount,
            )}
            {renderLimitsRow(
              t('fields.max_order_amount_short'),
              item.payinMaxOrderAmount,
            )}
          </Fragment>
        ),
        valueClassName: 'tw-w-[240px]',
      },
      {
        header: tCommon('common.payout'),
        valueGetter: (item) => (
          <Fragment>
            {renderLimitsRow(
              t('fields.min_order_amount_short'),
              item.payoutMinOrderAmount,
            )}
            {renderLimitsRow(
              t('fields.max_order_amount_short'),
              item.payoutMaxOrderAmount,
            )}
            {renderLimitsRow(
              t('fields.max_daily_amount_short'),
              item.payoutMaxDailyAmount,
            )}
            {renderLimitsRow(
              t('fields.max_active_orders_short'),
              item.payoutMaxActiveOrders || payoutMaxActiveOrders,
              item.payoutMaxActiveOrders
                ? undefined
                : t('fields.default_value'),
            )}
            {item.payoutRequisitesAutomationEnabled && (
              <div>{`${t(
                'fields.payout_requisites_automation_enabled_short',
              )}: ${formatUtils.formatBoolean(
                item.payoutRequisitesAutomationEnabled,
              )}`}</div>
            )}
          </Fragment>
        ),
        valueClassName: 'tw-w-[240px]',
      },
      ...(canManage
        ? [
            {
              header: t('fields.groups'),
              multiValueRenderer: {
                itemsGetter: (item: Trader) => item.groups,
                valueGetter: (group: Group) => (
                  <div key={group.id}>{group?.name}</div>
                ),
              },
            },
            dataGridColumns.getActionsColumn({
              handleEdit: (item: Trader) => handleEditClick(item.id),
            }),
          ]
        : []),
    ],
    [
      t,
      tCommon,
      canManage,
      getAgentName,
      renderLimitsRow,
      payoutMaxActiveOrders,
      handleEditClick,
    ],
  );

  const filtersDefinitions: FilterDefinition<TraderFilters>[] = useMemo(
    () => [
      {
        label: tCommon('filters.trader'),
        name: 'traderId',
        type: FilterDefinitionType.Trader,
        traders: queryResultTraders.data,
      },
      {
        label: t('filters.work_status'),
        name: 'workStatus',
        type: FilterDefinitionType.Enum,
        getDisplayName: traderUtils.getWorkStatusLabel,
        enum: TraderWorkStatus,
      },
      filtersUtils.getEnabledStatusFilter<TraderFilters>(
        t('filters.unable_take_orders'),
        'unableTakeOrders',
      ),
      {
        label: t('filters.group'),
        name: 'groupId',
        hidden: !canManage,
        type: FilterDefinitionType.Select,
        getDisplayName: (groupId: string) =>
          find(queryResultGroups.data, { id: groupId })?.name,
        options: formUtils.getOptions(queryResultGroups.data || []),
      } as FilterDefinition<TraderFilters>,
      {
        label: `${t('filters.compensation')} (${tCommon('common.payin')})`,
        type: FilterDefinitionType.Numeric,
        name: 'compensation',
      },
      {
        label: `${t('filters.compensation')} (${tCommon('common.payout')})`,
        type: FilterDefinitionType.Numeric,
        name: 'payoutCompensation',
      },
      filtersUtils.getEnabledStatusFilter<TraderFilters>(
        `${t('filters.payout_requisites_automation_enabled_short')} (${tCommon(
          'common.payout',
        )})`,
        'payoutRequisitesAutomationEnabled',
      ),
    ],
    [canManage, queryResultGroups.data, queryResultTraders.data, t, tCommon],
  );

  return (
    <Fragment>
      <CrudPage
        header={{ title: t('title') }}
        filters={{ filtersDefinitions }}
        table={{ queryResult, paginated: true, columns }}
      />
      <IntervalCommissionsInfoDialog
        {...intervalCommissionsInfoDialogProps}
        onClose={() => setIntervalCommissionsInfoDialogOpen({ open: false })}
      />
    </Fragment>
  );
};
